// src/pages/CommissionRecord.jsx
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BASE_API_URL } from "../helpers/Config";
import {
    Calendar,
    Filter,
    Info,
    RefreshCw,
    X,
    XCircle,
} from "lucide-react";

const CommissionRecord = ({ theme = "dark" }) => {
    const isDarkMode = theme === "dark";

    // -----------------------------
    // State
    // -----------------------------
    const [records, setRecords] = useState([]);
    const [type, setType] = useState("all"); // all | trade | refer | deposit | self deposit | ti coin buy
    const [filterDate, setFilterDate] = useState(""); // YYYY-MM-DD or ""
    const [lastId, setLastId] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [activeRow, setActiveRow] = useState(null);

    const scrollRef = useRef(null);
    const inFlightRef = useRef(false);
    const requestSeqRef = useRef(0);

    // -----------------------------
    // Theme tokens
    // -----------------------------
    const t = useMemo(() => {
        if (isDarkMode) {
            return {
                pageBg: "#111827",
                cardBg: "#020617",
                cardBgLight: "rgba(15, 23, 42, 0.9)",
                cardBorder: "#1f2937",
                text: "#f9fafb",
                textSoft: "#e5e7eb",
                muted: "#9ca3af",
                accent: "#6366f1",
                accentLight: "#a5b4fc",
                success: "#10b981",
                warning: "#f59e0b",
                danger: "#ef4444",
                shadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
                shadowLight: "0 8px 30px rgba(0, 0, 0, 0.3)",
                line: "rgba(148, 163, 184, 0.14)",
                buttonBg: "rgba(100, 116, 139, 0.22)",
                buttonBgHover: "rgba(100, 116, 139, 0.32)",
                modalBg: "rgba(15, 23, 42, 0.97)",
                backdrop: "rgba(0, 0, 0, 0.7)",
            };
        }
        return {
            pageBg: "#f8fafc",
            cardBg: "#ffffff",
            cardBgLight: "#ffffff",
            cardBorder: "rgba(203, 213, 225, 0.55)",
            text: "#0f172a",
            textSoft: "#475569",
            muted: "#94a3b8",
            accent: "#3b82f6",
            accentLight: "#60a5fa",
            success: "#10b981",
            warning: "#f59e0b",
            danger: "#ef4444",
            shadow: "0 20px 60px rgba(0, 0, 0, 0.08)",
            shadowLight: "0 8px 30px rgba(0, 0, 0, 0.06)",
            line: "rgba(203, 213, 225, 0.65)",
            buttonBg: "rgba(203, 213, 225, 0.25)",
            buttonBgHover: "rgba(203, 213, 225, 0.35)",
            modalBg: "rgba(255, 255, 255, 0.985)",
            backdrop: "rgba(0, 0, 0, 0.4)",
        };
    }, [isDarkMode]);

    // -----------------------------
    // Helpers
    // -----------------------------
    const getToken = () => {
        try {
            return localStorage.getItem("a5srf4wer7w1cd") || "";
        } catch {
            return "";
        }
    };

    const typeLabel = (v) => {
        if (!v) return "—";
        const s = String(v).trim().toLowerCase();
        if (s === "self deposit") return "Self Deposit";
        if (s === "ti coin buy") return "TI Coin Buy";
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    const getTypeColor = (v) => {
        const s = String(v || "").trim().toLowerCase();
        if (s === "trade") return "#8b5cf6"; // Purple
        if (s === "deposit") return "#10b981"; // Green
        if (s === "refer") return "#f59e0b"; // Amber
        if (s === "self deposit") return "#3b82f6"; // Blue
        if (s === "ti coin buy") return "#d4af37"; // Gold
        return "#64748b"; // Slate
    };

    const formatCoin = (coin) => {
        const num = Number(coin);
        if (isNaN(num)) return "0";
        // Keep up to 5 decimals but trim trailing zeros
        return num.toFixed(5).replace(/\.?0+$/, "");
    };

    // Required format: "Dec 23, 2025 10:38 pm"
    const formatDateTimeCard = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return "—";

            const datePart = d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });

            const timePart = d
                .toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                })
                .toLowerCase();

            return `${datePart} ${timePart}`;
        } catch {
            return "—";
        }
    };

    const buildDetails = (row) => {
        const claimedYesNo = row?.is_claimed ? "Yes" : "No";

        const base = {
            "Record ID": row?.id,
            "Date & Time": formatDateTimeCard(row?.date),
            Type: typeLabel(row?.type),
            Level: `L${row?.level}`,
            Coin: formatCoin(row?.coin),
            Claimed: claimedYesNo,
        };

        const s = String(row?.type || "").trim().toLowerCase();

        if (s === "trade" && row?.has_trade_data) {
            return {
                ...base,
                "User ID": row?.trade_data?.user_id,
                "User Level": row?.trade_data?.user_level,
                "Trade Coin": row?.trade_data?.trade_amount,
            };
        }

        if (s === "deposit" && row?.has_deposit_data) {
            return {
                ...base,
                "User ID": row?.deposit_data?.user_id,
                "User Level": row?.deposit_data?.user_level,
                "Deposit Coin": row?.deposit_data?.deposit_coin,
            };
        }

        if (s === "refer" && row?.has_refer_data) {
            return {
                ...base,
                "User ID": row?.refer_data?.user_id,
                "User Level": row?.refer_data?.user_level,
                "Deposit Coin": row?.refer_data?.deposit_coin,
            };
        }

        if (s === "self deposit" && row?.has_self_deposit_data) {
            return {
                ...base,
                User: "Self",
                "User ID": row?.deposit_data?.user_id,
                "User Level": row?.deposit_data?.user_level,
                "Deposit Coin": row?.deposit_data?.deposit_coin,
            };
        }

        return base;
    };

    // -----------------------------
    // API
    // -----------------------------
    const fetchRecords = useCallback(
        async ({ reset = false, pageLastId = 0 } = {}) => {
            const token = getToken();
            if (!token) {
                setError("Token not found. Please login again.");
                setLoading(false);
                setLoadingMore(false);
                setHasMore(false);
                return;
            }

            if (!reset && inFlightRef.current) return;

            const seq = ++requestSeqRef.current;
            if (!reset) inFlightRef.current = true;

            try {
                setError("");
                reset ? setLoading(true) : setLoadingMore(true);

                const payload = {
                    token,
                    last_id: reset ? 0 : pageLastId,
                    type: type === "all" ? "" : type,
                    date: filterDate || "",
                };

                const { data } = await axios.post(
                    `${BASE_API_URL}/team/commission-record`,
                    payload
                );

                if (seq !== requestSeqRef.current) return;

                const rows = Array.isArray(data?.data) ? data.data : [];
                const nextLastId =
                    typeof data?.last_id === "number"
                        ? data.last_id
                        : rows.length
                            ? rows[rows.length - 1].id
                            : reset
                                ? 0
                                : pageLastId;

                setHasMore(Boolean(data?.has_more));
                setLastId(nextLastId);

                if (reset) {
                    setRecords(rows);
                } else {
                    setRecords((prev) => {
                        const seen = new Set(prev.map((x) => x.id));
                        const merged = [...prev];
                        for (const r of rows) {
                            if (!seen.has(r.id)) merged.push(r);
                        }
                        return merged;
                    });
                }
            } catch (e) {
                console.error(e);
                if (seq !== requestSeqRef.current) return;
                setError(
                    e?.response?.data?.message ||
                    "Failed to load commission records. Please try again."
                );
            } finally {
                if (seq === requestSeqRef.current) {
                    setLoading(false);
                    setLoadingMore(false);
                }
                inFlightRef.current = false;
            }
        },
        [type, filterDate]
    );

    // Reset + refetch when type/date changes
    useEffect(() => {
        setRecords([]);
        setLastId(0);
        setHasMore(true);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        fetchRecords({ reset: true, pageLastId: 0 });
    }, [type, filterDate, fetchRecords]);

    // Infinite scroll (on the page wrapper)
    const onScroll = (e) => {
        const el = e.currentTarget;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 220;

        if (nearBottom && hasMore && !loading && !loadingMore) {
            fetchRecords({ reset: false, pageLastId: lastId });
        }
    };

    // -----------------------------
    // UI Actions
    // -----------------------------
    const openInfo = (row) => {
        setActiveRow(row);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setActiveRow(null);
    };

    const onRefresh = () => {
        setRecords([]);
        setLastId(0);
        setHasMore(true);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        fetchRecords({ reset: true, pageLastId: 0 });
    };

    const clearDate = () => setFilterDate("");

    // Close modal on ESC
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") closeModal();
        };
        if (showModal) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showModal]);

    // Prevent background scroll when modal open
    useEffect(() => {
        if (!showModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [showModal]);

    // -----------------------------
    // Main Render
    // -----------------------------
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="commission-page"
            style={{
                minHeight: "100vh",
                background: t.pageBg,
                color: t.text,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Global Styles */}
            <style>{`
                * { box-sizing: border-box; }

                .commission-scroll {
                    height: 100vh;
                    overflow-y: auto;
                    overscroll-behavior: contain;
                    -webkit-overflow-scrolling: touch;
                    background: ${t.pageBg};
                }

                .commission-container {
                    max-width: 500px;
                    margin: 0 auto;
                    min-height: 100%;
                    position: relative;
                    background: ${t.pageBg};
                }

                /* Header (NOT sticky) */
                .commission-header {
                    padding: 20px 16px 16px;
                    background: ${t.pageBg};
                    border-bottom: 1px solid ${t.line};
                }

                .header-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .title-section h1 {
                    font-size: 22px;
                    font-weight: 700;
                    margin: 0;
                    color: ${t.text};
                    letter-spacing: -0.2px;
                }

                .title-section p {
                    font-size: 13px;
                    color: ${t.muted};
                    margin: 4px 0 0;
                }

                .refresh-button {
                    background: ${t.buttonBg};
                    border: 1px solid ${t.cardBorder};
                    color: ${t.textSoft};
                    width: 40px;
                    height: 40px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .refresh-button:hover {
                    background: ${t.buttonBgHover};
                    transform: translateY(-1px);
                }

                .refresh-button:active {
                    transform: translateY(0);
                }

                /* Filters */
                .filters-container {
                    background: ${t.cardBg};
                    border: 1px solid ${t.cardBorder};
                    border-radius: 16px;
                    padding: 16px;
                    margin-bottom: 12px;
                    box-shadow: ${t.shadowLight};
                }

                .filter-group { margin-bottom: 12px; }
                .filter-group:last-child { margin-bottom: 0; }

                .filter-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12px;
                    font-weight: 700;
                    color: ${t.muted};
                    margin-bottom: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.35px;
                }

                .filter-select {
                    width: 100%;
                    padding: 12px 40px 12px 12px;
                    background: ${t.buttonBg};
                    border: 1px solid ${t.line};
                    border-radius: 12px;
                    color: ${t.text};
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    background-image:
                        linear-gradient(45deg, transparent 50%, ${t.muted} 50%),
                        linear-gradient(135deg, ${t.muted} 50%, transparent 50%);
                    background-position:
                        calc(100% - 18px) calc(50% - 3px),
                        calc(100% - 13px) calc(50% - 3px);
                    background-size: 6px 6px, 6px 6px;
                    background-repeat: no-repeat;
                }

                .commission-container.dark .filter-select,
                .commission-container.dark .date-input {
                    color-scheme: dark;
                }

                .commission-container.light .filter-select,
                .commission-container.light .date-input {
                    color-scheme: light;
                }

                .filter-select:focus {
                    outline: none;
                    border-color: ${t.accent};
                    box-shadow: 0 0 0 3px ${t.accent}1f;
                }

                .filter-select option {
                    background: ${t.cardBg};
                    color: ${t.text};
                }

                .date-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .date-input {
                    width: 100%;
                    padding: 12px 40px 12px 40px;
                    background: ${t.buttonBg};
                    border: 1px solid ${t.line};
                    border-radius: 12px;
                    color: ${t.text};
                    font-size: 14px;
                    transition: all 0.2s ease;
                }

                .date-input:focus {
                    outline: none;
                    border-color: ${t.accent};
                    box-shadow: 0 0 0 3px ${t.accent}1f;
                }

                .calendar-icon {
                    position: absolute;
                    left: 12px;
                    color: ${t.muted};
                    pointer-events: none;
                }

                .clear-date {
                    position: absolute;
                    right: 10px;
                    background: transparent;
                    border: none;
                    color: ${t.muted};
                    cursor: pointer;
                    padding: 4px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s ease, transform 0.15s ease;
                }

                .clear-date:hover {
                    color: ${t.textSoft};
                    transform: translateY(-1px);
                }

                .clear-date:active {
                    transform: translateY(0);
                }

                /* Records */
                .records-container {
                    padding: 0 16px 80px;
                }

                .records-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin: 14px 0 12px;
                    padding: 0 4px;
                }

                .records-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: ${t.textSoft};
                }

                .records-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .record-card {
                    background: ${t.cardBg};
                    border: 1px solid ${t.cardBorder};
                    border-radius: 16px;
                    padding: 16px;
                    cursor: pointer;
                    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
                    position: relative;
                    overflow: hidden;
                }

                .record-card:hover {
                    transform: translateY(-2px);
                    border-color: ${t.line};
                    box-shadow: ${t.shadowLight};
                }

                .record-card:active {
                    transform: translateY(0);
                }

                .record-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 12px;
                }

                .record-id {
                    font-size: 15px;
                    font-weight: 800;
                    color: ${t.text};
                    line-height: 1.1;
                }

                .record-date {
                    font-size: 12px;
                    color: ${t.muted};
                    margin-top: 4px;
                    line-height: 1.2;
                }

                .type-badge {
                    padding: 6px 12px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 0.2px;
                    white-space: nowrap;
                }

                /* Amount + Level on same line */
                .record-mainline {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding-top: 12px;
                    border-top: 1px solid ${t.line};
                }

                .mainline-left {
                    display: flex;
                    align-items: baseline;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .kv {
                    font-size: 12px;
                    color: ${t.muted};
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.25px;
                }

                .vv {
                    font-size: 15px;
                    color: ${t.text};
                    font-weight: 800;
                }

                .vv.amount {
                    color: ${t.accent};
                }

                .dot-sep {
                    width: 4px;
                    height: 4px;
                    border-radius: 999px;
                    background: ${t.muted};
                    opacity: 0.6;
                    transform: translateY(-2px);
                }

                .right-actions {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-shrink: 0;
                }

                /* Claimed Yes/No */
                .claimed-pill {
                    padding: 6px 10px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 800;
                    border: 1px solid transparent;
                    letter-spacing: 0.2px;
                }

                .info-button {
                    background: transparent;
                    border: 1px solid ${t.line};
                    color: ${t.muted};
                    width: 34px;
                    height: 34px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .info-button:hover {
                    background: ${t.buttonBg};
                    color: ${t.text};
                }

                /* Loading / Empty */
                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid ${t.line};
                    border-top-color: ${t.accent};
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 16px;
                }

                @keyframes spin { to { transform: rotate(360deg); } }

                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                }

                .empty-icon {
                    display: inline-flex;
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    align-items: center;
                    justify-content: center;
                    background: ${t.buttonBg};
                    border: 1px solid ${t.line};
                    margin-bottom: 14px;
                    color: ${t.muted};
                }

                .empty-text {
                    color: ${t.textSoft};
                    font-size: 14px;
                    font-weight: 700;
                    margin-bottom: 8px;
                }

                .empty-subtext {
                    color: ${t.muted};
                    font-size: 12px;
                    line-height: 1.45;
                }

                .load-more {
                    text-align: center;
                    padding: 20px;
                    color: ${t.muted};
                    font-size: 13px;
                }

                .no-more {
                    text-align: center;
                    padding: 16px 20px 0;
                    color: ${t.muted};
                    font-size: 13px;
                    font-style: italic;
                }

                /* Error */
                .error-card {
                    background: ${t.cardBg};
                    border: 1px solid ${t.danger}40;
                    border-radius: 16px;
                    padding: 14px 16px;
                    margin-top: 12px;
                }

                .error-title {
                    color: ${t.danger};
                    font-size: 13px;
                    font-weight: 800;
                    margin-bottom: 6px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .error-message {
                    color: ${t.textSoft};
                    font-size: 13px;
                    line-height: 1.4;
                }

                /* Modal */
                .modal-backdrop {
                    position: fixed;
                    inset: 0;
                    background: ${t.backdrop};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    z-index: 1000;
                    backdrop-filter: blur(4px);
                }

                .modal-container {
                    background: ${t.modalBg};
                    border-radius: 20px;
                    width: 100%;
                    max-width: 440px;
                    max-height: 85vh;
                    box-shadow: ${t.shadow};
                    border: 1px solid ${t.cardBorder};
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .modal-header {
                    padding: 18px 18px 14px;
                    border-bottom: 1px solid ${t.line};
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .modal-title {
                    font-size: 18px;
                    font-weight: 800;
                    color: ${t.text};
                    margin: 0;
                }

                .modal-subtitle {
                    font-size: 12px;
                    color: ${t.muted};
                    margin-top: 6px;
                    line-height: 1.25;
                }

                .modal-close {
                    background: ${t.buttonBg};
                    border: 1px solid ${t.line};
                    color: ${t.text};
                    width: 38px;
                    height: 38px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }

                .modal-close:hover {
                    background: ${t.buttonBgHover};
                    transform: translateY(-1px);
                }

                .modal-close:active {
                    transform: translateY(0);
                }

                .modal-body {
                    padding: 18px;
                    overflow-y: auto;
                    flex: 1;
                    min-height: 0; /* critical for flex+scroll */
                }

                .modal-section { margin-bottom: 20px; }
                .modal-section:last-child { margin-bottom: 0; }

                .section-title {
                    font-size: 13px;
                    font-weight: 800;
                    color: ${t.text};
                    margin-bottom: 12px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .section-title::before {
                    content: "";
                    width: 4px;
                    height: 16px;
                    background: ${t.accent};
                    border-radius: 2px;
                }

                .details-grid {
                    display: grid;
                    gap: 10px;
                }

                .detail-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 12px;
                    background: ${t.buttonBg};
                    border-radius: 12px;
                    border: 1px solid ${t.line};
                }

                .detail-key {
                    font-size: 13px;
                    color: ${t.muted};
                    font-weight: 700;
                }

                .detail-value-modal {
                    font-size: 13px;
                    color: ${t.text};
                    font-weight: 800;
                    text-align: right;
                    max-width: 62%;
                    word-break: break-word;
                }

                /* Scrollbar */
                .commission-scroll::-webkit-scrollbar,
                .modal-body::-webkit-scrollbar {
                    width: 6px;
                }

                .commission-scroll::-webkit-scrollbar-track,
                .modal-body::-webkit-scrollbar-track {
                    background: transparent;
                }

                .commission-scroll::-webkit-scrollbar-thumb,
                .modal-body::-webkit-scrollbar-thumb {
                    background: ${t.muted}45;
                    border-radius: 4px;
                }

                .commission-scroll::-webkit-scrollbar-thumb:hover,
                .modal-body::-webkit-scrollbar-thumb:hover {
                    background: ${t.muted}70;
                }
            `}</style>

            {/* Scroll wrapper so header/filters scroll away (NOT sticky) */}
            <div
                className="commission-scroll"
                ref={scrollRef}
                onScroll={onScroll}
            >
                <div
                    className={`commission-container ${isDarkMode ? "dark" : "light"
                        }`}
                >
                    {/* Header */}
                    <div className="commission-header">
                        <div className="header-top">
                            <div className="title-section">
                                <h1>Commission Records</h1>
                                <p>Track your team commissions and earnings</p>
                            </div>

                            <button
                                className="refresh-button"
                                onClick={onRefresh}
                                title="Refresh"
                                aria-label="Refresh"
                            >
                                <RefreshCw size={18} />
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="filters-container">
                            <div className="filter-group">
                                <div className="filter-label">
                                    <Filter size={14} />
                                    Type Filter
                                </div>
                                <select
                                    className="filter-select"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    <option value="deposit">Deposit</option>
                                    <option value="refer">Referral</option>
                                    <option value="self deposit">Self Deposit</option>
                                    <option value="ti coin buy">TI Coin Buy</option>
                                    <option value="trade">Trade</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <div className="filter-label">
                                    <Calendar size={14} />
                                    Date Filter
                                </div>
                                <div className="date-input-wrapper">
                                    <Calendar className="calendar-icon" size={16} />
                                    <input
                                        type="date"
                                        className="date-input"
                                        value={filterDate}
                                        onChange={(e) =>
                                            setFilterDate(e.target.value || "")
                                        }
                                        max={new Date().toISOString().split("T")[0]}
                                    />
                                    {filterDate && (
                                        <button
                                            className="clear-date"
                                            onClick={clearDate}
                                            title="Clear date filter"
                                            aria-label="Clear date filter"
                                        >
                                            <XCircle size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="error-card">
                                <div className="error-title">
                                    <XCircle size={16} />
                                    Error Loading Data
                                </div>
                                <div className="error-message">{error}</div>
                            </div>
                        )}
                    </div>

                    {/* Records */}
                    <div className="records-container">
                        <div className="records-header">
                            <div className="records-title">Commission History</div>
                            {/* Do NOT show total loaded records (removed) */}
                        </div>

                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner" />
                                <div className="empty-text">Loading records...</div>
                            </div>
                        ) : records.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon" aria-hidden="true">
                                    <Info size={22} />
                                </div>
                                <div className="empty-text">
                                    No commission records found
                                </div>
                                <div className="empty-subtext">
                                    {type !== "all" || filterDate
                                        ? "Try changing your filters."
                                        : "Commissions will appear here when available."}
                                </div>
                            </div>
                        ) : (
                            <AnimatePresence>
                                <div className="records-list pb-5">
                                    {records.map((record, index) => {
                                        const typeColor = getTypeColor(record.type);
                                        const isClaimed = Boolean(record.is_claimed);

                                        const claimedBg = isClaimed
                                            ? `${t.success}18`
                                            : `${t.danger}18`;

                                        const claimedBorder = isClaimed
                                            ? `${t.success}45`
                                            : `${t.danger}45`;

                                        const claimedColor = isClaimed
                                            ? t.success
                                            : t.danger;

                                        return (
                                            <motion.div
                                                key={record.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 8 }}
                                                transition={{ delay: index * 0.02 }}
                                                className="record-card"
                                                onClick={() => openInfo(record)}
                                                whileTap={{ scale: 0.985 }}
                                            >
                                                <div className="record-header">
                                                    <div>
                                                        <div className="record-id">
                                                            #{record.id}
                                                        </div>
                                                        <div className="record-date">
                                                            {formatDateTimeCard(
                                                                record.date
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="type-badge"
                                                        style={{
                                                            background: `${typeColor}18`,
                                                            color: typeColor,
                                                            border: `1px solid ${typeColor}35`,
                                                        }}
                                                    >
                                                        {typeLabel(record.type)}
                                                    </div>
                                                </div>

                                                {/* Amount and Level on the SAME line */}
                                                <div className="record-mainline">
                                                    <div className="mainline-left">
                                                        <span className="kv">
                                                            Coin:
                                                        </span>
                                                        <span className="vv amount">
                                                            {formatCoin(record.coin)}
                                                        </span>
                                                        <span className="dot-sep" />
                                                        <span className="vv">
                                                            L{record.level}
                                                        </span>
                                                    </div>

                                                    <div className="right-actions">
                                                        <div
                                                            className="claimed-pill"
                                                            style={{
                                                                background: claimedBg,
                                                                borderColor: claimedBorder,
                                                                color: claimedColor,
                                                            }}
                                                        >
                                                            {isClaimed ? "Claimed" : "Not Claimed"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </AnimatePresence>
                        )}

                        {loadingMore && (
                            <div className="load-more">
                                <div
                                    className="loading-spinner"
                                    style={{
                                        width: "20px",
                                        height: "20px",
                                        margin: "0 auto 8px",
                                        borderWidth: "3px",
                                    }}
                                />
                                Loading more records...
                            </div>
                        )}

                        {!loading && !loadingMore && records.length > 0 && !hasMore && (
                            <div className="no-more">No more records to load</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            <AnimatePresence>
                {showModal && activeRow && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-backdrop"
                        onClick={closeModal}
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 260, damping: 22 }}
                            className="modal-container"
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Commission Details"
                        >
                            <div className="modal-header">
                                <div>
                                    <h3 className="modal-title">Commission Details</h3>
                                    <div className="modal-subtitle">
                                        #{activeRow.id} •{" "}
                                        {formatDateTimeCard(activeRow.date)}
                                    </div>
                                </div>
                                <button
                                    className="modal-close"
                                    onClick={closeModal}
                                    aria-label="Close"
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* FIX: modal body scrollable (flex:1 + min-height:0) */}
                            <div className="modal-body">
                                <div className="modal-section">
                                    <div className="section-title">Summary</div>
                                    <div className="details-grid">
                                        {Object.entries(buildDetails(activeRow)).map(
                                            ([key, value], index) => (
                                                <motion.div
                                                    key={key}
                                                    className="detail-row"
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.035 }}
                                                >
                                                    <div className="detail-key">{key}</div>
                                                    <div className="detail-value-modal">
                                                        {String(value || "—")}
                                                    </div>
                                                </motion.div>
                                            )
                                        )}
                                    </div>
                                </div>

                                <div className="modal-section">
                                    <div className="section-title">Notes</div>
                                    <div
                                        className="detail-row"
                                        style={{
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                        }}
                                    >
                                        <div
                                            className="detail-key"
                                            style={{
                                                fontSize: "12px",
                                                color: t.muted,
                                                lineHeight: 1.6,
                                                fontWeight: 700,
                                            }}
                                        >
                                            • Commission records are updated in real-time.
                                            <br />
                                            • If Claimed is “No”, it may become claimable after verification.
                                            <br />
                                            • Contact support for any discrepancies.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default CommissionRecord;
